package com.downdogapp.client

import com.downdogapp.*
import com.downdogapp.client.api.*
import com.downdogapp.client.controllers.start.NewPracticePage
import com.downdogapp.client.singleton.*
import com.downdogapp.client.singleton.Key.DislikedPoseCount
import com.downdogapp.client.singleton.Key.LikedPoseCount
import kotlin.math.abs
import kotlin.random.Random

object SequenceSettings : LogEmitter {

  // Sequence repsonse entry is a pair of Response,isValid
  // We mark a sequence invalid when we want a new one but don't want to throw it out yet
  private val sequenceLengthsResponses =
    mutableMapOf<Map<Int, Int>, CachedResponse?>()
  private val sequenceLengthsResponseCallbacks =
    mutableMapOf<Map<Int, Int>, List<(SequenceLengthsResponse?) -> Unit>>()
  private var sequenceLengthsResponsesKey = 0
  private val cachedTypeIdToSettingNode = mutableMapOf<Int, SettingNode>()
  private var cachedSelectorSections = listOf<SettingSelectorSection>()
  private var cachedSequenceSettingsMap = mapOf<Int, Int>()
  private var cachedPlaylistSettingsMap = mapOf<Int, Int>()

  val playlistSettings get() = cachedPlaylistSettingsMap

  val lengthOptionIds
    get() = getOptions(MANIFEST.sequenceLengthTypeId).map { it.id }

  val selectedLengthOptionId
    get() = getSelectedId(MANIFEST.sequenceLengthTypeId) ?: lengthOptionIds.firstOrNull() ?: 5

  val selectorSections
    get() =
      cachedSelectorSections

  val allSelectorTypeIds: List<Int>
    get() =
      selectorSections
        .flatMap { it.items }
        .map { it.typeId }

  fun onManifestFetched() {
    resetCache()
    maybeReapplyMixPreset()
    sequenceLengthsResponsesKey = Random.nextInt()
    sequenceLengthsResponses.clear()
    maybeFetchSequences()
  }

  fun resetCache() {
    cachedTypeIdToSettingNode.clear()
    for (root in MANIFEST.settingRoots) {
      for (selected in getAllSelectedNodes(root)) {
        cachedTypeIdToSettingNode[selected.typeId] = selected
      }
    }

    cachedSelectorSections =
      MANIFEST
        .selectorSections
        .map { section ->
          SettingSelectorSection(
            title = section.title,
            items = section.items.filter {
              (it.typeId == MANIFEST.mixPresetTypeId && getMixConfig() != null)
              || (it.typeId == MANIFEST.viewPosesTypeId && displayViewPoses())
              || getOptions(it.typeId).size > 1
            }
          )
        }
        .filter {
          it.items.isNotEmpty()
        }

    cachedPlaylistSettingsMap =
      cachedTypeIdToSettingNode
        .filter {
          it.value.typeId in MANIFEST.playlistSettingTypeIds
        }
        .map { it.key to getSelectedId(it.value) }
        .filter { it.second != null }
        .associate { it.first to it.second!! }

    cachedSequenceSettingsMap =
      cachedTypeIdToSettingNode
        .filter {
          it.value.typeId !in MANIFEST.playlistSettingTypeIds
          && it.value.typeId != MANIFEST.sequenceLengthTypeId
          && it.value.typeId != MANIFEST.viewPosesTypeId
        }
        .map { it.key to getSelectedId(it.value) }
        .filter { it.second != null }
        .associate { it.first to it.second!! } +
      (getMixConfig()
         ?.let { config ->
           val onlyGroupId = getMixOnlyGroupId(config)
           config
             .groupIds
             .associateWith { groupId ->
               when (onlyGroupId) {
                 null -> getMixGroupAmount(groupId)
                 groupId -> 100
                 else -> 0
               }
             } +
           config
             .subgroupIds
             .associateWith {
               if (getMixSubgroupEnabled(it))
                 1
               else
                 0
             } +
           (UserPrefs[Key.MixPreset]
              ?.let { presetId ->
                mapOf(MANIFEST.mixPresetTypeId to presetId)
              }
            ?: mapOf()) +
           (UserPrefs[Key.CustomMix]
              ?.let { custom ->
                mapOf(MANIFEST.customMixTypeId to if (custom) 1 else 0)
              }
            ?: mapOf())
         }
       ?: mapOf()) +
      MANIFEST.allPoseIds.associateNotNull { poseId ->
        getPoseLiked(poseId)?.let { liked ->
          poseId to if (liked) MANIFEST.likedStateLikedId else MANIFEST.likedStateDisikedId
        }
      }
  }

  private fun getAllSelectedNodes(root: SettingNode): List<SettingNode> =
    (getSelectedOption(root)
       ?.children
       ?.flatMap {
         getAllSelectedNodes((it))
       }
     ?: listOf()) +
    root

  fun getRecentSelectorTypeIds(num: Int): List<Int> {
    val flatTypes = allSelectorTypeIds
    val recents =
      UserPrefs[Key.RecentSettingSelectorTypeIds].filter { it in flatTypes }

    if (recents.size < num) {
      return recents + flatTypes.filter { it !in recents }.take(num - recents.size)
    }
    return recents.take(num)
  }

  fun saveSetting(typeId: Int, optionId: Int) {
    UserPrefs[Key.SequenceSetting(typeId)] = optionId
    logEvent(
      "update_setting",
      mapOf(
        "type_id" to typeId.toString(),
        "option_id" to optionId.toString()
      )
    )
    resetCache()
    maybeReapplyMixPreset()
    ensureSomeMixSubgroupsEnabled()
    maybeFetchSequences()
  }

  fun resetCacheAndMaybeFetchSequences() {
    resetCache()
    maybeFetchSequences()
  }

  private fun maybeFetchSequences() {
    if (sequenceLengthsResponses[cachedSequenceSettingsMap] == null) {
      fetchSequences()
    }
  }

  fun fetchSequences() {
    // We mark this invalid here, since if we want a new sequence it means we'd never want to use whatever we already had
    val settingsMap = cachedSequenceSettingsMap
    if (settingsMap[0] == null) {
      Logger.logError(
        "missing_category",
        cachedSequenceSettingsMap
          .associate { it.key.toString() to it.value.toString() } +
        mapOf("sequenceLengthsResponsesKey" to sequenceLengthsResponsesKey.toString())
      )
    }
    sequenceLengthsResponses[settingsMap]?.isValid = false

    val key = sequenceLengthsResponsesKey
    Network.post(SequenceLengthsRequest(settingsMap)) {
      if (sequenceLengthsResponsesKey == key) {
        val response = it?.let { SequenceLengthsResponse.fromJson(it) }
        sequenceLengthsResponses[settingsMap] = response?.let { CachedResponse(it, true) }
        sequenceLengthsResponseCallbacks.remove(settingsMap)?.forEach { it(response) }
      } else {
        sequenceLengthsResponseCallbacks.remove(settingsMap)?.forEach { it(null) }
      }
      NewPracticePage.refreshPage()
      logEvent("sequences_fetched")
    }
  }

  fun saveSelectorToRecents(selectorTypeId: Int) {
    UserPrefs[Key.RecentSettingSelectorTypeIds] =
      listOf(selectorTypeId) +
      UserPrefs[Key.RecentSettingSelectorTypeIds].filter { it != selectorTypeId }
  }

  fun useSequenceResponse(callback: (SequenceLengthsResponse?) -> Unit) {
    val settingsMap = cachedSequenceSettingsMap
    if (settingsMap in sequenceLengthsResponses && sequenceLengthsResponses[settingsMap]?.isValid == true) {
      callback(sequenceLengthsResponses[settingsMap]?.response)
    } else {
      sequenceLengthsResponseCallbacks[settingsMap] =
        (sequenceLengthsResponseCallbacks[settingsMap] ?: listOf()) + callback
    }
  }

  fun getTypeLabel(typeId: Int) =
    getSelectorItem(typeId)?.label

  fun getSelectorTitle(typeId: Int) =
    getSelectorItem(typeId)?.let {
      it.selectorTitle ?: it.label
    }

  fun getSelectorSubtitle(typeId: Int) =
    getSelectorItem(typeId)?.selectorSubtitle

  fun getOptions(typeId: Int): List<SettingOption> =
    getSetting(typeId)?.let { getSelectableOptions(it) } ?: listOf()

  fun getSelectedId(typeId: Int): Int? =
    getSetting(typeId)?.let { getSelectedId(it) }

  fun getSelectedLabel(typeId: Int): String? =
    when (typeId) {
      MANIFEST.mixPresetTypeId ->
        getMixConfig()?.let { config ->
          getSelectedMixPreset(config)?.label
          ?: getMixOnlyGroupId(config)?.let { groupId ->
            Strings.ONLY_MIX_GROUP(MANIFEST.mixGroups.find { it.id == groupId }!!.label)
          }
          ?: Strings.CUSTOM
        }

      MANIFEST.viewPosesTypeId -> {
        sequenceLengthsResponses[cachedSequenceSettingsMap]?.response?.allPoses?.let { poses ->
          UserPrefs[LikedPoseCount] = poses.count { it.isLikeable && getPoseLiked(it.poseId) == true }
          UserPrefs[DislikedPoseCount] = poses.count { it.isLikeable && getPoseLiked(it.poseId) == false }
        }

        Strings.LIKED_DISLIKED_COUNT(UserPrefs[LikedPoseCount] ?: 0, UserPrefs[DislikedPoseCount] ?: 0)
      }

      else ->
        getSetting(typeId)?.let { setting ->
          getSelectedOption(setting)?.let {
            getOptionLabel(typeId, it)
          }
        }
    }

  fun getMixSubgroup(subgroupId: Int) = MANIFEST.mixSubgroups.find { it.id == subgroupId }

  fun getMixOnlyGroupId(config: MixConfig) =
    UserPrefs[Key.MixOnlyGroup]?.let { onlyGroupId ->
      if (onlyGroupId in config.groupIds)
        onlyGroupId
      else
        null
    }

  fun setMixOnlyGroup(groupId: Int?) {
    if (groupId == null) {
      UserPrefs.remove(Key.MixOnlyGroup)
    } else {
      logEvent("mix_only_selectoed", mapOf("group_id" to groupId.toString()))
      UserPrefs[Key.MixOnlyGroup] = groupId
    }
  }

  fun getMixGroupAmount(groupId: Int) = UserPrefs[Key.MixGroupAmount(groupId)] ?: 0

  fun setMixGroupAmount(groupId: Int, amount: Int) {
    UserPrefs[Key.MixGroupAmount(groupId)] = amount
    UserPrefs[Key.CustomMix] = true
  }

  fun getMixSubgroupEnabled(subgroupId: Int) =
    UserPrefs[Key.MixSubgroupEnabled(subgroupId)] ?: true

  fun setMixSubgroupEnabled(subgroupId: Int, value: Boolean) {
    UserPrefs[Key.MixSubgroupEnabled(subgroupId)] = value
    UserPrefs[Key.CustomMix] = true
  }

  fun getPoseLiked(poseId: Int) =
    when (UserPrefs[Key.SequenceSetting(poseId)]) {
      MANIFEST.likedStateLikedId -> true
      MANIFEST.likedStateDisikedId -> false
      else -> null
    }

  fun setPoseLiked(poseId: Int, liked: Boolean?) {
    when (UserPrefs[Key.SequenceSetting(poseId)]) {
      MANIFEST.likedStateLikedId -> UserPrefs[LikedPoseCount] = (UserPrefs[LikedPoseCount] ?: 0) - 1
      MANIFEST.likedStateDisikedId -> UserPrefs[DislikedPoseCount] = (UserPrefs[DislikedPoseCount] ?: 0) - 1
      else -> {}
    }
    if (liked == null) {
      UserPrefs.remove(Key.SequenceSetting(poseId))
    } else {
      if (liked) {
        UserPrefs[LikedPoseCount] = (UserPrefs[LikedPoseCount] ?: 0) + 1
      } else {
        UserPrefs[DislikedPoseCount] = (UserPrefs[DislikedPoseCount] ?: 0) + 1
      }
      UserPrefs[Key.SequenceSetting(poseId)] =
        if (liked) MANIFEST.likedStateLikedId else MANIFEST.likedStateDisikedId
    }
  }

  fun getSelectedLength(): String? =
    getSetting(MANIFEST.sequenceLengthTypeId)?.let { getSelectedOption(it)?.label }

  fun getOptionLabel(typeId: Int, option: SettingOption) =
    if (typeId == MANIFEST.sequenceLengthTypeId) Strings.MINUTES(option.label) else option.label

  fun getStoredId(typeId: Int) =
    getSetting(typeId)?.let { UserPrefs[Key.SequenceSetting(it.typeId)] }

  fun getSelectorItem(typeId: Int) =
    selectorSections
      .flatMap { it.items }
      .find { it.typeId == typeId }

  private fun getSetting(typeId: Int) =
    cachedTypeIdToSettingNode[typeId]

  fun getMixConfig(): MixConfig? {
    cachedTypeIdToSettingNode
      .values
      .forEach {
        getSelectedOption(it)?.mixConfig?.let {
          return it
        }
      }
    return null
  }

  private fun displayViewPoses(): Boolean {
    cachedTypeIdToSettingNode
      .values
      .forEach {
        getSelectedOption(it)?.displayViewPoses?.let {
          return it
        }
      }
    return false
  }

  private fun ensureSomeMixSubgroupsEnabled() {
    getMixConfig()?.let { config ->
      val groupIdToSubgroupIds =
        config.subgroupIds.groupBy { subgroupId ->
          MANIFEST.mixSubgroups.find { it.id == subgroupId }!!.groupId
        }
      for (groupId in config.groupIds) {
        groupIdToSubgroupIds[groupId]?.let { subgroupIds ->
          if (subgroupIds.none { getMixSubgroupEnabled(it) }) {
            logEvent("set_all_subgroups_enabled", mapOf("group_id" to groupId.toString()))
            for (subgroupId in subgroupIds) {
              setMixSubgroupEnabled(subgroupId, true)
            }
          }
        }
      }
    }
  }

  fun getSelectedMixPreset(config: MixConfig) =
    if (UserPrefs[Key.CustomMix] == true)
      null
    else
      UserPrefs[Key.MixPreset].let { id ->
        if (id in config.presetIds)
          MANIFEST.mixPresets.find { it.id == id }
        else
          null
      }

  fun setSelectedMixPreset(preset: MixPreset) {
    logEvent("mix_selected_preset", mapOf("preset_id" to preset.id.toString()))
    val config = getMixConfig()!!
    UserPrefs[Key.MixPreset] = preset.id
    val enabledSubgroupIds = config.subgroupIds.filter { it in preset.mixSubgroupsEnabled }
    for (groupId in config.groupIds) {
      setMixGroupAmount(
        groupId = groupId,
        amount = if (enabledSubgroupIds.any { getMixSubgroup(it)?.groupId == groupId })
          preset.mixGroupAmounts[groupId] ?: 0
        else
          0
      )
    }
    for (subgroupId in config.subgroupIds) {
      setMixSubgroupEnabled(subgroupId, subgroupId in enabledSubgroupIds)
    }
    setMixOnlyGroup(
      config
        .groupIds
        .singleOrNull { (preset.mixGroupAmounts[it] ?: 0) > 0 }
    )
    UserPrefs[Key.CustomMix] = false
  }

  private fun maybeReapplyMixPreset() {
    if (UserPrefs[Key.CustomMix] != true) {
      getMixConfig()?.let { config ->
        val presetId = UserPrefs[Key.MixPreset]
        val preset =
          (if (presetId in config.presetIds)
            MANIFEST.mixPresets.find { it.id == presetId }
          else
            null)
          ?: MANIFEST.mixPresets.first { it.id in config.presetIds }
        setSelectedMixPreset(preset)
      }
    }
  }

  private fun getSelectableOptions(setting: SettingNode): List<SettingOption> {
    if (setting.typeId == MANIFEST.sequenceLengthTypeId) {
      val response = sequenceLengthsResponses[cachedSequenceSettingsMap]?.response
      if (response != null && response.sequenceTokens.isNotEmpty()) {
        return setting.options.filter { it.id in response.sequenceTokens }
      }
    }

    return setting.options
  }

  private fun getSelectedId(setting: SettingNode): Int? {
    val selectableOptionIds = getSelectableOptions(setting).map { it.id }
    if (selectableOptionIds.isEmpty()) {
      return null
    }

    val allOptionIds = setting.options.map { it.id }

    val stored = UserPrefs[Key.SequenceSetting(setting.typeId)]

    fun get(idealId: Int): Int? {
      if (idealId in selectableOptionIds) {
        return idealId
      }
      if (setting.typeId == MANIFEST.sequenceLengthTypeId && idealId in allOptionIds) {
        val index = allOptionIds.indexOf(idealId)
        return selectableOptionIds.minBy { abs(allOptionIds.indexOf(it) - index) }
      }
      return null
    }

    return stored?.let { get(it) }
           ?: setting.defaultId?.let { get(it) }
           ?: selectableOptionIds.first()
  }

  private fun getSelectedOption(setting: SettingNode) =
    getSelectedId(setting)?.let { selectedId ->
      setting.options.firstOrNull { it.id == selectedId }
    }

  private data class CachedResponse(val response: SequenceLengthsResponse, var isValid: Boolean)
}
